import { useCallback, useEffect, useReducer, useState, useSyncExternalStore } from 'react'
import { ActionType, type Chart } from 'klinecharts'
import { getTimeframeSeconds } from '@/utils/chartUtils'
import type { CanvasSeries } from './canvasSeries'
import type { TradeIdea } from './tradeIdeas'

/**
 * Previous / next trade idea: two arrows that take the chart to the ideas on it, for a trader
 * who panned away and lost them. Navigation only — nothing is opened, selected or edited.
 *
 * The jump is a pan: the same scroll a drag would make, at the user's zoom. It reads the canvas
 * series and never writes it, bar the paging a drag to the left edge would also have done — so a
 * walkthrough replay on the canvas sees nothing it has not seen from a pan before.
 */

/** Where an idea sits on the time axis (ms). `mid` is what a jump centres — as revealIdea does. */
export interface IdeaAnchor {
  ideaId: string
  lo: number
  hi: number
  mid: number
}

/**
 * The ideas a jump can go to, in time order.
 *
 * Skipped: an idea whose every level the panel's eye has hidden (there is nothing to look at),
 * and — while a replay cuts the chart — an idea with any level past the last revealed bar, which
 * the replay has hidden until the playhead gets there (the latest-point rule of
 * replayDrawingVisibility).
 */
export function ideaAnchors(ideas: TradeIdea[], { lastRevealedMs = null }: { lastRevealedMs?: number | null } = {}): IdeaAnchor[] {
  const out: IdeaAnchor[] = []
  for (const idea of ideas) {
    if (idea.marks.every((m) => m.hidden)) continue
    const stamps = idea.marks.map((m) => m.timestamp).filter((t) => Number.isFinite(t) && t > 0)
    if (stamps.length === 0) continue
    const lo = Math.min(...stamps)
    const hi = Math.max(...stamps)
    if (lastRevealedMs != null && hi > lastRevealedMs) continue
    out.push({ ideaId: idea.ideaId, lo, hi, mid: (lo + hi) / 2 })
  }
  return out.sort((a, b) => a.mid - b.mid || a.ideaId.localeCompare(b.ideaId))
}

/**
 * `ts` as a fractional bar index into `bars` (ascending ms timestamps): interpolated between
 * the bars around it, so a weekend gap is crossed in one bar rather than two days' worth; and
 * extrapolated by the timeframe outside the loaded bars, which keeps an idea older or newer than
 * the window in order.
 */
export function floatIndexOf(bars: readonly number[], ts: number, tfMs: number): number {
  const n = bars.length
  if (n === 0) return 0
  if (ts <= bars[0]) return (ts - bars[0]) / tfMs
  if (ts >= bars[n - 1]) return n - 1 + (ts - bars[n - 1]) / tfMs
  let lo = 0
  let hi = n - 1
  while (hi - lo > 1) {
    const m = (lo + hi) >> 1
    if (bars[m] <= ts) lo = m
    else hi = m
  }
  return lo + (ts - bars[lo]) / (bars[hi] - bars[lo])
}

/**
 * Bars either side of the chart's centre an idea must be to count as "previous" or "next".
 * A jump lands the idea within a bar of the centre, so a centred idea is neither — a second
 * press goes on to the following one instead of re-centring this.
 */
export const NAV_TOLERANCE_BARS = 1.5

/** The nearest idea left and right of the chart's centre (a float bar index). */
export function navTargets(
  anchors: IdeaAnchor[],
  centreIdx: number,
  indexOf: (ts: number) => number,
): { prev: IdeaAnchor | null; next: IdeaAnchor | null } {
  let prev: IdeaAnchor | null = null
  let next: IdeaAnchor | null = null
  for (const a of anchors) {
    const i = indexOf(a.mid)
    if (i < centreIdx - NAV_TOLERANCE_BARS) prev = a
    else if (i > centreIdx + NAV_TOLERANCE_BARS && !next) next = a
  }
  return { prev, next }
}

const barTimes = (chart: Chart): number[] => chart.getDataList().map((b) => b.timestamp)

/** Bars that fit across the candle pane at the current zoom (fractional). */
function screenBars(chart: Chart): number {
  const width = chart.getSize('candle_pane', 'main' as Parameters<Chart['getSize']>[1])?.width ?? 0
  const barSpace = chart.getBarSpace()
  return width > 0 && barSpace > 0 ? width / barSpace : 0
}

/**
 * The bar index under the chart's horizontal centre, or null before any bars have landed.
 *
 * The engine puts bar k at x = width − (n + d − k − 0.5)·barSpace, d being its offset from the
 * last bar; `realTo` is round(d + n + 0.5), unclamped past the data. So the centre sits at
 * d + n − 0.5 − screen/2 ≈ realTo − 1 − screen/2, to within half a bar.
 */
function centreIndex(chart: Chart): number | null {
  if (chart.getDataList().length === 0) return null
  return chart.getVisibleRange().realTo - 1 - screenBars(chart) / 2
}

/** Identifies a view, to tell whether the chart has moved since a jump. */
function viewKey(chart: Chart): string {
  const vr = chart.getVisibleRange()
  return `${chart.getDataList().length}:${vr.realFrom}:${vr.realTo}:${chart.getBarSpace()}`
}

/**
 * Centre `ts` on screen at the current zoom. scrollToDataIndex(t) sets d = t − (n − 1), which by
 * the formula above puts index t + 0.5 − screen/2 under the centre — so aim screen/2 − 0.5 past
 * the idea. A real scroll, so the chart's OnScroll moves the notes and cards with it. A no-op
 * under a drawing tool's scroll lock, so the lock is lifted around it — as
 * CanvasSeries.setHeikinAshi does.
 */
function centreOn(chart: Chart, ts: number, tfMs: number) {
  const idx = floatIndexOf(barTimes(chart), ts, tfMs)
  const locked = !chart.isScrollEnabled()
  if (locked) chart.setScrollEnabled(true)
  chart.scrollToDataIndex(idx + screenBars(chart) / 2 - 0.5)
  if (locked) chart.setScrollEnabled(false)
}

const noSubscribe = () => () => {}
const zero = () => 0

export interface UseIdeaNavigationOptions {
  chart: Chart | null
  series: CanvasSeries | null
  ideas: TradeIdea[]
  timeframe: string
}

/**
 * The arrows' state and actions. Recomputed on every render of its own consumer — which
 * re-renders on the series' data (a page, a replay reveal) and on the chart's scroll and zoom,
 * never the whole Workbench.
 */
export function useIdeaNavigation({ chart, series, ideas, timeframe }: UseIdeaNavigationOptions) {
  useSyncExternalStore(series?.subscribe ?? noSubscribe, series?.getVersion ?? zero)
  const [, bump] = useReducer((n: number) => n + 1, 0)
  useEffect(() => {
    if (!chart) return undefined
    const onMove = () => bump()
    chart.subscribeAction(ActionType.OnScroll, onMove)
    chart.subscribeAction(ActionType.OnZoom, onMove)
    return () => {
      chart.unsubscribeAction(ActionType.OnScroll, onMove)
      chart.unsubscribeAction(ActionType.OnZoom, onMove)
    }
  }, [chart])
  const [busy, setBusy] = useState(false)
  // The idea the last jump went to, and the view it left. An idea near either end of the data
  // cannot be centred — the pan bounds stop the scroll short — so until the chart moves again
  // that idea stands for the centre: at the last idea, › greys out instead of jumping in place.
  const [landed, setLanded] = useState<{ mid: number; key: string } | null>(null)

  const tfMs = getTimeframeSeconds(timeframe) * 1000

  /** Where the arrows point right now — read fresh, from the chart as it is. */
  const targets = useCallback(() => {
    if (!chart) return { prev: null, next: null, count: 0 }
    const cut = series?.getCutoff() != null
    const lastSec = cut ? series!.getAppliedLastTs() : null
    const anchors = ideaAnchors(ideas, { lastRevealedMs: lastSec != null ? lastSec * 1000 : null })
    let centre = centreIndex(chart)
    if (centre == null) return { prev: null, next: null, count: anchors.length }
    const bars = barTimes(chart)
    const indexOf = (ts: number) => floatIndexOf(bars, ts, tfMs)
    if (landed && landed.key === viewKey(chart)) centre = indexOf(landed.mid)
    return { ...navTargets(anchors, centre, indexOf), count: anchors.length }
  }, [chart, series, ideas, tfMs, landed])

  const go = useCallback(
    async (dir: 'prev' | 'next') => {
      if (!chart || busy) return
      const target = targets()[dir]
      if (!target) return
      setBusy(true)
      try {
        const first = chart.getDataList()[0]?.timestamp
        if (series && first != null && target.lo < first) {
          // Older than the loaded history: page it in first, as a drag to the left edge would.
          await series.ensureFrom(Math.floor(target.lo / 1000))
        } else if (series && series.getCutoff() == null && !series.reachesAnchor()) {
          // The window was moved into the past (a walkthrough, a trade row) and ends before the
          // idea: load around it. Never while a replay cuts the chart — its ideas past the
          // playhead are not targets, so everything a target needs is already loaded.
          const list = chart.getDataList()
          const last = list[list.length - 1]?.timestamp
          if (last != null && target.hi > last) await series.loadAround(Math.floor(target.mid / 1000))
        }
        centreOn(chart, target.mid, tfMs)
        setLanded({ mid: target.mid, key: viewKey(chart) })
      } finally {
        setBusy(false)
        bump()
      }
    },
    [chart, series, busy, targets, tfMs],
  )

  const t = targets()
  return {
    count: t.count,
    canPrev: !busy && !!t.prev,
    canNext: !busy && !!t.next,
    goPrev: () => void go('prev'),
    goNext: () => void go('next'),
  }
}
